import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { ContractsService } from '../../../services/contracts.service';
import { VehiclesService } from '../../../services/vehicles.service';
import { VehicleAttachment } from '../../../models/VehicleAttachment';
import {
  VEHICLE_DOCUMENT_TAG_OPTIONS,
  VEHICLE_DOCUMENT_TYPE_OPTIONS
} from '../../../models/vehicle-document-types';
import { ErrorService } from '../../../shared/services/error.service';

@Component({
  selector: 'app-b2b-contracts-vehicle-view',
  templateUrl: './b2b-contracts-vehicle-view.component.html',
  styleUrls: ['./b2b-contracts-vehicle-view.component.css']
})
export class B2bContractsVehicleViewComponent implements OnInit {
  isLoading = false;
  contractId: number | null = null;
  itemId: number | null = null;
  snapshotId: number | null = null;
  contractNumber = '';
  contractName = '';
  itemNumber = '';
  vehicle: any = null;
  driverHistory: any[] = [];
  isLoadingDriverHistory = false;

  attachments: VehicleAttachment[] = [];
  isLoadingAttachments = false;
  fleetVehicleId: number | null = null;

  private readonly documentTypeOptions = VEHICLE_DOCUMENT_TYPE_OPTIONS;
  private readonly tagOptions = VEHICLE_DOCUMENT_TAG_OPTIONS;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private vehiclesService: VehiclesService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.contractId = +params['id'];
      this.itemId = +params['itemId'];
      this.snapshotId = +params['snapshotId'];
      if (this.contractId && this.itemId && this.snapshotId) {
        this.load();
      }
    });
  }

  load(): void {
    if (!this.contractId || !this.itemId || !this.snapshotId) return;
    this.isLoading = true;

    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (contract) => {
        this.contractNumber = contract.contractNumber || '';
        this.contractName = contract.contractName || '';
      },
      error: () => {}
    });

    this.contractsService.getB2bContractItemById(this.contractId, this.itemId).subscribe({
      next: (item) => {
        this.itemNumber = item?.itemNumber || item?.ItemNumber || String(this.itemId);
        this.vehicle = this.mapVehicleFromItem(item, this.snapshotId!);
        this.isLoading = false;
        if (!this.vehicle) {
          this.errorService.showErrorMessage('Veículo não encontrado neste pedido.');
          this.navigateBack();
          return;
        }
        this.loadDriverHistory();
        this.loadVehicleAttachments();
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoading = false;
      }
    });
  }

  private loadDriverHistory(): void {
    if (!this.contractId || !this.itemId || !this.snapshotId) return;
    this.isLoadingDriverHistory = true;
    this.contractsService
      .listarHistoricoB2bItemVeiculo(this.contractId, this.itemId, this.snapshotId)
      .pipe(finalize(() => (this.isLoadingDriverHistory = false)))
      .subscribe({
        next: (rows) => {
          this.driverHistory = (rows || []).map((row: any) => ({
            id: row.id ?? row.Id,
            driverName: row.driverName ?? row.DriverName ?? 'Não informado',
            driverCpf: row.driverCpf ?? row.DriverCpf ?? '',
            startDate: row.startDate ?? row.StartDate,
            endDate: row.endDate ?? row.EndDate,
            status: row.status ?? row.Status ?? '',
            notes: row.notes ?? row.Notes ?? ''
          }));
        },
        error: () => {
          this.driverHistory = [];
        }
      });
  }

  /** Documentos do cadastro da frota (VehicleAttachments), não do snapshot B2B. */
  private loadVehicleAttachments(): void {
    this.attachments = [];
    this.fleetVehicleId = null;
    if (!this.vehicle) return;

    const fromSnapshot = Number(this.vehicle.fleetVehicleId);
    if (fromSnapshot > 0) {
      this.fleetVehicleId = fromSnapshot;
      this.fetchAttachments(fromSnapshot);
      return;
    }

    const plate = String(this.vehicle.plate || '')
      .trim()
      .replace(/\s+/g, '')
      .toUpperCase();
    if (!plate || plate === 'N/A') return;

    this.isLoadingAttachments = true;
    this.vehiclesService.getByPlate(plate).subscribe({
      next: (fleet) => {
        const id = Number(fleet?.id);
        if (!(id > 0)) {
          this.isLoadingAttachments = false;
          return;
        }
        this.fleetVehicleId = id;
        this.fetchAttachments(id);
      },
      error: () => {
        this.isLoadingAttachments = false;
        this.attachments = [];
      }
    });
  }

  private fetchAttachments(vehicleId: number): void {
    this.isLoadingAttachments = true;
    this.vehiclesService
      .listAttachments(vehicleId)
      .pipe(finalize(() => (this.isLoadingAttachments = false)))
      .subscribe({
        next: (list) => {
          this.attachments = Array.isArray(list) ? list : [];
        },
        error: () => {
          this.attachments = [];
        }
      });
  }

  downloadAttachment(att: VehicleAttachment): void {
    if (!att) return;
    this.vehiclesService.downloadAttachment(att).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.displayAttachmentName(att);
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {}
    });
  }

  attachmentSubtitle(att: VehicleAttachment): string {
    if (att.attachmentType) {
      const byType = this.documentTypeOptions.find((o) => o.value === att.attachmentType);
      if (byType) return byType.label;
      return att.attachmentType;
    }
    if (att.tags) {
      try {
        const t = JSON.parse(att.tags) as { documentType?: string; tag?: string; prefix?: string };
        const parts: string[] = [];
        if (t.documentType) {
          const opt = this.documentTypeOptions.find((o) => o.value === t.documentType);
          parts.push(opt ? opt.label : t.documentType);
        }
        if (t.tag) {
          const tagOpt = this.tagOptions.find((o) => o.value === t.tag);
          parts.push(tagOpt ? tagOpt.label : t.tag);
        }
        if (t.prefix) parts.push(`Placa: ${t.prefix}`);
        if (parts.length) return parts.join(' · ');
      } catch {
        /* ignore */
      }
    }
    return '';
  }

  formatAttachmentSize(size: number | undefined | null): string {
    if (size == null || Number.isNaN(Number(size))) return 'Tamanho não disponível';
    const n = Number(size);
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  displayAttachmentName(att: VehicleAttachment): string {
    const name = (att.fileName || '').trim();
    if (name && !name.startsWith('file_')) return name;
    if (name.includes('_')) {
      const original = name.split('_').slice(2).join('_');
      if (original) return original;
    }
    return name || 'anexo';
  }

  private mapVehicleFromItem(item: any, snapshotId: number): any | null {
    const vehicles = item?.vehicleSnapshots || item?.VehicleSnapshots || [];
    const v = vehicles.find((row: any) => {
      const rawId =
        row.id ??
        row.Id ??
        row.b2BContractItemVehicleSnapshotId ??
        row.B2BContractItemVehicleSnapshotId;
      return Number(rawId) === snapshotId;
    });
    if (!v) return null;

    const assocs = item.driverAssociations || item.DriverAssociations || [];
    const hasAssoc = assocs.some(
      (x: any) =>
        Number(x.b2BContractItemVehicleSnapshotId ?? x.B2BContractItemVehicleSnapshotId) === snapshotId
    );
    const hasDriver = !!(v.hasDriver ?? v.HasDriver ?? false) || hasAssoc;
    const assoc = assocs.find(
      (x: any) =>
        Number(x.b2BContractItemVehicleSnapshotId ?? x.B2BContractItemVehicleSnapshotId) === snapshotId
    );
    const driverName =
      assoc?.driverName ||
      assoc?.DriverName ||
      assoc?.condutorNome ||
      (assoc?.driver && (assoc.driver.name || assoc.driver.Name)) ||
      null;

    const fleetVehicleIdRaw = v.vehicleId ?? v.VehicleId ?? null;
    const fleetVehicleId = Number(fleetVehicleIdRaw);

    return {
      id: snapshotId,
      fleetVehicleId: fleetVehicleId > 0 ? fleetVehicleId : null,
      plate: v.plate || v.Plate || 'N/A',
      model: v.model || v.Model || 'N/A',
      year: v.year || v.Year || 'N/A',
      color: v.color || v.Color || 'N/A',
      brand: v.brand || v.Brand || 'N/A',
      renavam: v.renavam || v.Renavam || 'N/A',
      chassis: v.chassis || v.Chassis || 'N/A',
      currentKm: v.currentKm ?? v.CurrentKm ?? 0,
      inspectionNotes: v.inspectionNotes || v.InspectionNotes || '',
      trackerType: v.trackerType || v.TrackerType || null,
      trackerId: v.trackerId || v.TrackerId || null,
      hasBox: !!(v.hasBox ?? v.HasBox),
      hasPhoneSupport: !!(v.hasPhoneSupport ?? v.HasPhoneSupport),
      operationalStatus: v.operationalStatus || v.OperationalStatus || null,
      hasDriver,
      driverDisplay: hasDriver ? (driverName ? String(driverName) : 'Atribuído') : '—'
    };
  }

  navigateBack(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'vehicles']);
      return;
    }
    this.router.navigate(['/b2b/contracts']);
  }

  navigateToPedido(): void {
    if (this.contractId && this.itemId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'items', this.itemId, 'view']);
      return;
    }
    this.navigateBack();
  }

  formatKm(value: number): string {
    if (!value && value !== 0) return '0 km';
    return `${Number(value).toLocaleString('pt-BR')} km`;
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return String(dateString);
    }
  }

  formatCpf(cpf: string | null | undefined): string {
    const digits = String(cpf || '').replace(/\D/g, '');
    if (digits.length !== 11) return cpf || '—';
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  formatDriverHistoryStatus(status: string | null | undefined): string {
    const value = String(status || '').trim().toLowerCase();
    if (value === 'active' || value === 'ativo') return 'Ativo';
    if (value === 'inactive' || value === 'inativo') return 'Encerrado';
    if (value === 'replaced' || value === 'substituido' || value === 'substituído') return 'Substituído';
    if (value === 'pending' || value === 'pendente') return 'Pendente';
    return status || '—';
  }

  formatDriverHistoryStatusClass(status: string | null | undefined): string {
    const value = String(status || '').trim().toLowerCase();
    if (value === 'active' || value === 'ativo') return 'badge bg-success';
    if (value === 'inactive' || value === 'inativo') return 'badge bg-secondary';
    if (value === 'replaced' || value === 'substituido' || value === 'substituído') {
      return 'badge bg-warning text-dark';
    }
    return 'badge bg-light text-dark';
  }
}
